// File: src/hooks/usePermissions.js
// Version: 1.0 (2025-10-24)
// Description: Хук для централизованной проверки прав пользователя на основе ролей.
import { useAuth } from '../context/AuthContext'; // Импортируем хук useAuth для получения данных пользователя

/**
 * Хук для централизованной проверки прав пользователя на основе его ролей.
 *
 * @returns {object} Объект с булевыми флагами прав доступа и функцией hasPermission.
 */
export const usePermissions = () => {
    // Получаем текущего пользователя из контекста аутентификации
    const { user } = useAuth();
    // Извлекаем имена ролей пользователя.
    // Если пользователь есть и у него есть роли, получаем массив имен ролей.
    // Иначе используем пустой массив.
    // Преобразуем массив имен ролей в Set для быстрой проверки наличия роли (roles.has()).
    const roles = new Set(user?.roles?.map(role => (typeof role === 'string' ? role : role?.name)) || []);

    // --- Основные флаги ролей ---
    // Проверяем, есть ли у пользователя роль 'CLIENT'
    const isClient = roles.has('CLIENT');
    // Проверяем, является ли пользователь сотрудником (одна из ролей MP, Attorney, Paralegal)
    const isStaff = roles.has('MANAGING_PARTNER') || roles.has('ATTORNEY') || roles.has('PARALEGAL');
    // Проверяем, есть ли у пользователя роль 'ADMINISTRATOR', 'SUPERADMIN', или 'CREATOR'
    const isAdmin = roles.has('ADMINISTRATOR') || roles.has('SUPERADMIN') || roles.has('CREATOR');
    const isCreator = roles.has('CREATOR');
    const isSuperAdmin = roles.has('SUPERADMIN');
    // Проверяем, есть ли у пользователя роль 'MANAGING_PARTNER'
    const isManagingPartner = roles.has('MANAGING_PARTNER');

    // --- Флаги прав доступа (определяем конкретные разрешения) ---
    // Право на управление пользователями (есть у MP, Admin, Superadmin, Creator)
    const canManageUsers = isManagingPartner || isAdmin;

    // Право на просмотр профилей клиентов (есть у MP или Admin)
    const canViewClientProfile = isManagingPartner || isAdmin;

    // Право на просмотр общей статистики (есть у Staff или Admin)
    const canViewStats = isStaff || isAdmin;

    // --- ДОБАВЛЕНИЕ: Функция для проверки конкретного права ---
    /**
     * Проверяет, имеет ли текущий пользователь указанное право.
     * @param {string} permission - Название права (например, 'case:assign').
     * @returns {boolean} - True, если право есть, иначе False.
     */
    const hasPermission = (permission) => {
        // Здесь должна быть ваша реальная логика проверки прав.
        // Это может включать проверку ролей или специфических разрешений,
        // которые могут приходить с бэкенда вместе с данными пользователя.
        //
        // Пример упрощенной логики на основе ролей:
        switch (permission) {
            case 'case:assign': // Право назначать команду на дело
                return isManagingPartner; // Только MP может назначать
            case 'case:view:team': // Право просматривать назначенную команду
                return isStaff || isManagingPartner || isAdmin; // Сотрудники, MP, Admin
            case 'case:view:all': // Право просматривать все дела
                 return isManagingPartner || isAdmin; // Только MP и Admin
            // Добавьте другие кейсы для других прав...
            default:
                return false; // По умолчанию права нет
        }
    };
    // --- КОНЕЦ ДОБАВЛЕНИЯ ---


    // Возвращаем объект, содержащий как флаги ролей, так и флаги конкретных прав,
    // а также сам Set ролей и новую функцию hasPermission.
    return {
        // Флаги ролей
        isClient,
        isStaff,
        isAdmin,
        isManagingPartner,

        // Флаги прав доступа (как булевы значения)
        canManageUsers,
        canViewClientProfile,
        canViewStats,

        // Функция для проверки конкретных прав
        hasPermission, // <-- ДОБАВЛЕНО

        // Вспомогательные данные
        roles, // Set со всеми ролями пользователя
    };
};